import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ModalProductComponent } from '@base/components/modal-product/modal-product.component';
import { GlobalState } from '@base/models/others/global-state';
import { SearchReason } from '@base/models/others/match-reason';
import { InformaMfeMatchContext } from '@base/models/others/informa-mfe-event';
import { ProductDetail } from '@base/models/responses/product-detail.res';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { GlobalService } from '@base/services/private/global.service';
import { MeetService } from '@base/services/private/meet.service';
import { SeoService } from '@base/services/private/seo.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { JubliaAiService } from '@base/services/private/jublia-ai.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize, Subject, take, takeUntil } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';

@Component({
  selector: 'app-product-detail',
  imports: [],
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailComponent implements AfterViewInit {
  @ViewChild('productDetailContainer', { read: ViewContainerRef }) productDetailContainer!: ViewContainerRef;
  @Input() descriptionMatchQuery: string | null = null;
  private readonly judySrv = inject(JubliaAiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cancelRequests$ = new Subject<void>();
  private descriptionReasons: SearchReason[] = [];
  private isDescriptionMatchLoading = false;
  private routeLeft = false;

  @Input() modalId = '';
  @Input() productId: string | null = null;
  @Input() searchReasons: SearchReason[] | null = null;
  @Input() matchContext: InformaMfeMatchContext | null = null;

  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly meetSrv = inject(MeetService);
  private readonly seoSrv = inject(SeoService);
  private readonly globalSrv = inject(GlobalService);

  productDetailInstance: ComponentRef<unknown> | undefined;
  productDetail: ProductDetail | null = null;
  globalState: GlobalState | null = null;

  isShowPlaceholder = true;

  constructor() {
    inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.routeLeft = true;
        this.cancelRequests$.next();
      });
  }

  ngAfterViewInit(): void {
    (async () => {
      await this.loadProductDetailComponent();
    })();
  }

  /**
   * Fetches the product detail by `productId` and updates the component state.
   *
   * @returns void
   */
  getProductDetail(): void {
    const productId = this.productId;
    if (!productId || this.destroyRef.destroyed || this.routeLeft) return;
    this.cancelRequests$.next();
    this.descriptionReasons = [];
    this.productDetail = null;
    this.isShowPlaceholder = true;
    const query = this.descriptionMatchQuery?.trim();

    if (query) this.getDescriptionMatches(productId, query);

    this.meetSrv
      .getProductDetail(productId)
      .pipe(take(1), takeUntil(this.cancelRequests$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (this.productId !== productId) return;
          if (!res || res.status === 0 || !res.is_viewable) {
            this.cancelRequests$.next();
            this.globalSrv.destroyModalRef(this.modalId);
            return;
          }
          this.isShowPlaceholder = false;
          const reasons = this.searchReasons ?? res.search_reasons;
          this.productDetail = {
            ...res,
            search_reasons: this.descriptionReasons.length ? [...(reasons ?? []), ...this.descriptionReasons] : reasons,
          };
          this.renderProductDetail();
          this.setupProductDetailMetaTags();
        },
        error: (err) => {
          console.error('Error fetching product details:', err);
          this.cancelRequests$.next();
          this.isShowPlaceholder = false;
          this.globalSrv.destroyModalRef(this.modalId);
        },
      });
  }

  private getDescriptionMatches(productId: string, query: string): void {
    this.setDescriptionMatchLoading(true);
    this.judySrv
      .getDescriptionMatches({
        entity: 'products',
        search_query: query,
        description_match_ids: [productId],
      })
      .pipe(
        take(1),
        takeUntil(this.cancelRequests$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setDescriptionMatchLoading(false))
      )
      .subscribe({
        next: (res) => {
          if (this.productId !== productId) return;
          const phrases = res?.description_matches?.[productId];
          if (!Array.isArray(phrases)) return;
          this.descriptionReasons = phrases
            .filter((phrase): phrase is string => typeof phrase === 'string' && !!phrase.trim())
            .map((phrase) => ({ value: phrase.trim(), field_name: 'description', is_shown_in_listing: false }));
          if (!this.productDetail || this.descriptionReasons.length === 0) return;
          this.productDetail = {
            ...this.productDetail,
            search_reasons: [...(this.productDetail.search_reasons ?? []), ...this.descriptionReasons],
          };
          const modal = this.productDetailInstance?.instance as ModalProductComponent | undefined;
          modal?.updateSearchReasons(this.productDetail.search_reasons ?? []);
        },
        error: () => console.error('Failed to fetch product description matches.'),
      });
  }

  private setDescriptionMatchLoading(isLoading: boolean): void {
    this.isDescriptionMatchLoading = isLoading;
    const modal = this.productDetailInstance;
    if (!this.destroyRef.destroyed && modal && !modal.hostView?.destroyed) {
      modal.setInput('isDescriptionMatchLoading', isLoading);
    }
  }

  /**
   * Sets up SEO meta tags for the product detail page.
   */
  setupProductDetailMetaTags() {
    if (!this.productDetail) return;

    const metaTagObjectArray = [
      {
        property: 'og:url',
        content: window.location.href,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:title',
        content: String(this.productDetail?.title ?? ''),
      },
      {
        property: 'og:description',
        content: String(this.productDetail?.description ?? ''),
      },
      {
        property: 'og:image',
        content: String(this.productDetail?.image_url ?? ''),
      },
    ];

    this.seoSrv.updateMetaTags(metaTagObjectArray);
  }

  /**
   * Dynamically loads the product detail component into the container.
   *
   * @returns A Promise that resolves when the component has been loaded.
   */
  async loadProductDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.productDetailContainer,
        WhiteLabel.COMPONENTS.ModalProduct
      );
      if (this.destroyRef.destroyed) return;
      if (component) {
        this.productDetailInstance = component.componentRef || undefined;
        if (this.matchContext) {
          this.productDetailInstance?.setInput('matchContext', this.matchContext);
        }
        this.getProductDetail();
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Updates and passes input values to the dynamically loaded `ModalProductComponent`.
   *
   * @returns void
   */
  renderProductDetail(): void {
    const component = this.productDetailInstance?.instance as ModalProductComponent;
    if (component) {
      this.productDetailInstance?.setInput('productId', this.productId);
      this.productDetailInstance?.setInput('productDetail', this.productDetail);
      this.productDetailInstance?.setInput('isShowPlaceholder', this.isShowPlaceholder);
      this.productDetailInstance?.setInput('isDescriptionMatchLoading', this.isDescriptionMatchLoading);
      this.productDetailInstance?.setInput('modalId', this.modalId);
      if (this.matchContext) {
        this.productDetailInstance?.setInput('matchContext', this.matchContext);
      }
    }
  }
}
